import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { AudioDictationService } from 'src/app/shared/services/audio-dictation.service';
import { PipelineActivityLogService } from 'src/app/shared/services/pipeline-activity-log.service';
import { MedicalNecessityPdfService } from 'src/app/shared/services/medical-necessity-pdf.service';
import { ClinicalDocumentsPdfService } from 'src/app/shared/services/clinical-documents-pdf.service';
import { DictationActivityEvent } from 'src/app/shared/models';

@Component({
  selector: 'app-transcription-modal',
  templateUrl: './transcription-modal.component.html',
  styleUrls: ['./transcription-modal.component.scss'],
})
export class TranscriptionModalComponent implements OnInit, OnDestroy {
  @Input() responseData: any;
  @Input() patientInfo: any; // Add patient information input
  @Input() billingAnalysis: any;

  private _transcriptionData: any;
  @Input() set transcriptionData(value: any) {
    this._transcriptionData = value;
  }
  get transcriptionData(): any {
    return this._transcriptionData;
  }
  parsedData: any;
  @Input() dictationId: string;
  @Input() isArchivedSwitchChecked: boolean = false;
  @Input() recentVersion: boolean = false;
  @Input() transcription_id: string = '';
  @Input() actionItems: any[] = [];
  @Input() patientID: string;
  @Input() patientName: string = '';
  @Input() patientMrn: string = '';
  @Input() providerName: string = '';
  @Input() dateOfService: string = '';
  archiveInProgress = false;
  billingData: any = null;
  billingLoading = false;
  Assessmentpoints: string[] = [];
  recommendationsData: {
    tests: any[];
    medications: any[];
    treatments: any[];
    risk_factors: any[];
    considerations: string;
  } = {
      tests: [],
      medications: [],
      treatments: [],
      risk_factors: [],
      considerations: '',
    };

  @ViewChild('orders') orders: ElementRef;
  @ViewChild('doctorNotes') doctorNotes: ElementRef;
  @ViewChild('recommendations') recommendations: ElementRef;
  @ViewChild('transcription') transcription: ElementRef;

  activityEvents: DictationActivityEvent[] = [];
  activityLoading = false;
  private activityEventSource?: EventSource;

  constructor(
    private toastr: ToastrService,
    public activeModal: NgbActiveModal,
    private spinnerService: NgxSpinnerService,
    private audioDictationService: AudioDictationService,
    private pipelineActivityLogService: PipelineActivityLogService,
    private medicalNecessityPdfService: MedicalNecessityPdfService,
    private clinicalDocumentsPdfService: ClinicalDocumentsPdfService
  ) { }

  async ngOnInit(): Promise<void> {
    try {
      if (!this.responseData) {
        this.getDoctorNotes();
      } else {
        this.setParsedData(this.responseData);
      }
    } catch (error) {
      console.error('Error parsing JSON:', error);
      this.toastr.error('Failed to parse response data');
    }

    if (this.billingAnalysis) {
      try {
        this.billingData = typeof this.billingAnalysis === 'string'
          ? JSON.parse(this.billingAnalysis)
          : this.billingAnalysis;
        this.sortBillingCodes(this.billingData);
      } catch {
        this.fetchBillingAnalysis();
      }
    } else {
      this.fetchBillingAnalysis();
    }

    this.loadActivityHistory();
  }

  ngOnDestroy(): void {
    this.activityEventSource?.close();
  }

  private loadActivityHistory(): void {
    if (!this.dictationId) return;
    this.activityLoading = true;
    this.pipelineActivityLogService
      .getHistoryByDictation(this.dictationId)
      .then((res) => {
        this.activityEvents = res?.data ?? [];
        this.activityLoading = false;
        this.subscribeToActivityStream();
      })
      .catch((err) => {
        console.error('Failed to load activity log history:', err);
        this.activityLoading = false;
      });
  }

  private subscribeToActivityStream(): void {
    this.activityEventSource = this.pipelineActivityLogService.streamByDictation(this.dictationId);
    this.activityEventSource.addEventListener('activity-log', (event: MessageEvent) => {
      try {
        this.activityEvents.push(JSON.parse(event.data));
      } catch (err) {
        console.error('Failed to parse activity-log SSE event:', err);
      }
    });
  }

  getDoctorNotes = (): void => {
    if (this.transcriptionData) {
      this.audioDictationService
        .getDoctorNotesBySplitPrompts(this.transcriptionData)
        .then((res) => {
          if (res.code === 200) {
            this.responseData = this.safeJsonParse(res.message);
            this.setParsedData(this.responseData);
          }
        })
        .catch((err) => {
          this.spinnerService.hide();
          console.error(err);
          this.toastr.error(err.message);
        });
    }
  };

  private setParsedData(data: any): void {
    this.parsedData = this.safeJsonParse(data);
    this.setAssessmentPoints();
    this.setRecommendationsData();
  }

  private setAssessmentPoints(): void {
    this.Assessmentpoints = [];
    const assessmentPlan = this.parsedData?.doctor_notes?.['Assessment/Plan'];
    if (!assessmentPlan) return;

    if (Array.isArray(assessmentPlan)) {
      this.Assessmentpoints = assessmentPlan.filter(
        (point) => point && point.trim && point.trim().length > 0
      );
      return;
    }

    if (typeof assessmentPlan === 'string') {
      this.Assessmentpoints = assessmentPlan
        .split(/(?:\.\s(?=\d+\.\s))|(?:\n(?=\d+\.\s))/)
        .map((point) => point.trim())
        .filter((point) => point.length > 0);
    }
  }

  private setRecommendationsData(): void {
    const recSource =
      this.parsedData?.recommendations ??
      this.parsedData?.recommendation ??
      this.parsedData?.Recommendations ??
      this.parsedData?.doctor_notes?.recommendations ??
      this.parsedData?.doctor_notes?.recommendation ??
      this.parsedData?.doctor_notes?.Recommendations ??
      {};

    const recommendations = this.safeJsonParse(recSource);

    this.recommendationsData = {
      tests: this.normalizeRecommendationEntries(
        this.toArray(
          recommendations?.tests ??
          recommendations?.Tests ??
          recommendations?.test ??
          recommendations?.Test
        ),
        'tests'
      ),
      medications: this.normalizeRecommendationEntries(
        this.toArray(
          recommendations?.medications ??
          recommendations?.Medications ??
          recommendations?.medication ??
          recommendations?.Medication
        ),
        'medications'
      ),
      treatments: this.normalizeRecommendationEntries(
        this.toArray(
          recommendations?.treatments ??
          recommendations?.Treatments ??
          recommendations?.treatment ??
          recommendations?.Treatment
        ),
        'treatments'
      ),
      risk_factors: this.normalizeRecommendationEntries(
        this.toArray(
          recommendations?.risk_factors ??
          recommendations?.riskFactors ??
          recommendations?.risk_factor ??
          recommendations?.['Risk Factors']
        ),
        'risk_factors'
      ),
      considerations: this.toText(
        recommendations?.considerations ?? recommendations?.Considerations
      ),
    };
  }

  private toArray(value: any): any[] {
    const parsed = this.safeJsonParse(value);
    if (Array.isArray(parsed)) return parsed;
    if (parsed === null || parsed === undefined || parsed === '') return [];
    if (typeof parsed === 'object') return [parsed];
    if (typeof parsed === 'string') {
      const parts = parsed
        .split(/\n|;/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0);
      return parts.length ? parts : [parsed];
    }
    return [parsed];
  }

  private toText(value: any): string {
    const parsed = this.safeJsonParse(value);
    if (typeof parsed === 'string') return parsed;
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0)
        .join(', ');
    }
    if (parsed && typeof parsed === 'object') {
      return Object.values(parsed)
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter((item) => item.length > 0)
        .join(', ');
    }
    return '';
  }

  private normalizeRecommendationEntries(entries: any[], type: string): any[] {
    return entries
      .map((entry) => this.normalizeRecommendationEntry(entry, type))
      .filter((entry) => entry.primary || entry.secondary || entry.related_to);
  }

  private normalizeRecommendationEntry(entry: any, type: string): any {
    if (entry === null || entry === undefined) {
      return { related_to: '', primary: '', secondary: '' };
    }

    if (typeof entry === 'string' || typeof entry === 'number') {
      return {
        related_to: '',
        primary: String(entry),
        secondary: '',
      };
    }

    const related_to = this.toText(entry.related_to ?? entry.relatedTo ?? '');

    let primary = '';
    let secondary = '';

    if (type === 'tests') {
      primary = this.toText(entry.name ?? entry.test_name ?? entry.test ?? entry.title);
      secondary = this.toText(entry.reason ?? entry.description);
    } else if (type === 'medications') {
      primary = this.toText(entry.name ?? entry.medication ?? entry.drug ?? entry.title);
      secondary = [entry.dosage, entry.purpose, entry.reason]
        .map((item) => this.toText(item))
        .filter((item) => item.length > 0)
        .join(' | ');
    } else if (type === 'treatments') {
      primary = this.toText(entry.name ?? entry.treatment ?? entry.title);
      secondary = this.toText(entry.description ?? entry.reason ?? entry.purpose);
    } else {
      primary = this.toText(entry.factor ?? entry.risk ?? entry.name ?? entry.title);
      secondary = this.toText(entry.description ?? entry.reason);
    }

    if (!primary) {
      primary = Object.values(entry)
        .map((item) => this.toText(item))
        .filter((item) => item.length > 0)
        .slice(0, 2)
        .join(' - ');
    }

    return { related_to, primary, secondary };
  }

  private safeJsonParse(value: any): any {
    if (typeof value !== 'string') return value;
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  copySection(section: string): void {
    let textToCopy = '';
    switch (section) {
      case 'orders':
        textToCopy = this.orders?.nativeElement?.innerText || '';
        break;
      case 'doctorNotes':
        textToCopy = this.doctorNotes?.nativeElement?.innerText || '';
        break;
      case 'recommendations':
        textToCopy = this.recommendations?.nativeElement?.innerText || '';
        break;
      case 'transcription':
        textToCopy = this.transcription?.nativeElement?.innerText || '';
        break;
    }
    if (!textToCopy) return;
    this.copyTextToClipboard(textToCopy);
  }

  copyAll(): void {
    const ordersText = this.orders.nativeElement.innerText;
    const doctorNotesText = this.doctorNotes.nativeElement.innerText;
    const recommendationsText = this.recommendations.nativeElement.innerText;
    const allText = `${ordersText}\n\n${doctorNotesText}\n\n${recommendationsText}`;
    this.copyTextToClipboard(allText);
  }

  archiveDictation(): void {
    if (!this.dictationId) {
      this.toastr.error('No dictation ID provided.');
      return;
    }
    this.archiveInProgress = true;
    this.spinnerService.show();
    // If switch is checked, unarchive (false), else archive (true)
    const shouldArchive = !this.isArchivedSwitchChecked;
    this.audioDictationService
      .updateAudioDictation(this.dictationId, shouldArchive)
      .then(() => {
        this.spinnerService.hide();
        this.archiveInProgress = false;
        this.toastr.success(
          shouldArchive
            ? 'The dictation was archived.'
            : 'The dictation was unarchived.'
        );
        this.activeModal.close('archived');
      })
      .catch(() => {
        this.spinnerService.hide();
        this.archiveInProgress = false;
        this.toastr.error(
          shouldArchive
            ? 'Failed to archive the dictation.'
            : 'Failed to unarchive the dictation.'
        );
      });
  }

  fetchBillingAnalysis(): void {
    this.billingLoading = true;
    this.audioDictationService
      .getBillingAnalysis(this.transcriptionData, this.transcription_id)
      .then((res) => {
        this.billingData = res?.data ?? res;
        this.sortBillingCodes(this.billingData);
        this.billingLoading = false;
      })
      .catch((err) => {
        console.error('Billing analysis error:', err);
        this.billingLoading = false;
      });
  }

  private escRtf(s: any): string {
    return String(s ?? '')
      .replace(/\\/g, '\\\\')
      .replace(/\{/g, '\\{')
      .replace(/\}/g, '\\}')
      .replace(/[^\x00-\x7F]/g, c => {
        const code = c.charCodeAt(0);
        return code <= 0xFF
          ? `\\'${code.toString(16).padStart(2, '0')}`
          : `\\u${code}?`;
      });
  }

  private rtfTitle(text: string): string {
    return `\\pard\\sa120\\sb120\\b\\fs32 ${this.escRtf(text)}\\b0\\fs24\\par\\pard\\brdrb\\brdrs\\brdrw10\\brsp20 \\par\\pard\\sa80 `;
  }

  private rtfHeading(text: string): string {
    return `\\pard\\sa80\\sb160\\b\\fs26 ${this.escRtf(text)}\\b0\\fs24\\par`;
  }

  private rtfPara(text: string): string {
    return `\\pard\\sa80\\fs24 ${this.escRtf(text)}\\par`;
  }

  private rtfBullet(text: string): string {
    return `\\pard\\li360\\fi-180\\sa60\\fs24 \\bullet  ${this.escRtf(text)}\\par`;
  }

  private rtfSeparator(): string {
    return `\\pard\\sa80\\brdrb\\brdrs\\brdrw5\\brsp20 \\par\\pard\\sa80 `;
  }

  private rtfSubheading(text: string): string {
    return `\\pard\\sa60\\sb120\\b\\fs24 ${this.escRtf(text)}\\b0\\par`;
  }

  private rtfLabelValue(label: string, value: string): string {
    return `\\pard\\sa60\\fs22 \\b ${this.escRtf(label)}:\\b0  ${this.escRtf(value)}\\par`;
  }

  private htmlToRtf(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return this.nodeToRtf(doc.body);
  }

  private nodeToRtf(node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? '').replace(/\s+/g, ' ').trim();
      return text ? this.escRtf(text) : '';
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map(c => this.nodeToRtf(c)).join('');

    switch (tag) {
      case 'h1':
        return `\\pard\\sa80\\sb160\\b\\fs30 ${this.escRtf(el.textContent?.trim() ?? '')}\\b0\\fs24\\par`;
      case 'h2':
        return this.rtfSubheading(el.textContent?.trim() ?? '');
      case 'p': {
        const txt = el.textContent?.replace(/\s+/g, ' ').trim() ?? '';
        return txt ? this.rtfPara(txt) : '';
      }
      case 'li':
        return this.rtfBullet(el.textContent?.replace(/\s+/g, ' ').trim() ?? '');
      case 'ul':
      case 'ol':
        return children;
      case 'table':
        return this.tableToRtf(el);
      case 'section':
        return children + this.rtfSeparator();
      case 'br':
        return '\\line ';
      case 'body':
      case 'div':
      case 'html':
      case 'head':
      case 'style':
        return tag === 'head' || tag === 'style' ? '' : children;
      default:
        return children;
    }
  }

  private tableToRtf(table: Element): string {
    let rtf = '';
    const rows = Array.from(table.querySelectorAll('tr'));
    rows.forEach((row, rowIdx) => {
      const cells = Array.from(row.querySelectorAll('th, td'));
      const cellTexts = cells.map(c => (c.textContent ?? '').replace(/\s+/g, ' ').trim());
      if (cellTexts.every(t => !t)) return;
      const line = cellTexts.join('  |  ');
      if (rowIdx === 0) {
        rtf += `\\pard\\sa40\\sb80\\b\\fs22 ${this.escRtf(line)}\\b0\\par`;
        rtf += `\\pard\\sa0\\brdrb\\brdrs\\brdrw10\\brsp10 \\par`;
      } else {
        rtf += `\\pard\\sa40\\fs22 ${this.escRtf(line)}\\par`;
      }
    });
    return rtf;
  }

  private saveRtf(bodyContent: string, filename: string): void {
    const rtf = `{\\rtf1\\ansi\\ansicpg1252\\deff0{\\fonttbl{\\f0\\froman\\fcharset0 Arial;}}{\\colortbl ;\\red7\\green55\\blue134;}\\widowctrl\\widctlpar\\fs24\\f0\\lang1033 ${bodyContent}}`;
    const blob = new Blob([rtf], { type: 'application/rtf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  downloadActionItemLcdRtf(item: any): void {
    this.spinnerService.show();
    this.audioDictationService.getSixMonthsOrtMasters(this.patientID)
      .then((resp) => {
        this.spinnerService.hide();
        const patientNotes: any[] = Array.isArray(resp?.data?.ortMasters) ? resp.data.ortMasters : [];
        const order = item?.order;

        // ── Document title ────────────────────────────────────────────────────
        let body = this.rtfTitle('Medical Necessity Coverage Report');

        // ── Patient Info ──────────────────────────────────────────────────────
        body += this.rtfHeading('Patient Information');
        if (this.patientName) body += this.rtfLabelValue('Patient Name', this.patientName);
        if (this.patientMrn)  body += this.rtfLabelValue('MRN', this.patientMrn);
        if (this.providerName) body += this.rtfLabelValue('Provider', this.providerName);
        body += this.rtfSeparator();

        // ── Action Item ───────────────────────────────────────────────────────
        if (item?.action_item) {
          body += this.rtfHeading('Action Item');
          body += this.rtfPara(item.action_item);
          body += this.rtfSeparator();
        }

        // ── Medical Necessity ─────────────────────────────────────────────────
        body += this.rtfHeading('Medical Necessity');
        if (order?.lcd_coverage) body += this.rtfPara(order.lcd_coverage);

        let details: any = order?.lcd_coverage_details;
        if (details) {
          if (typeof details === 'string') {
            try { details = JSON.parse(details); } catch { /* keep as string */ }
          }
          body += this.rtfSubheading('Coverage Details');
          if (typeof details === 'string') {
            body += this.rtfPara(details);
          } else if (typeof details === 'object' && details !== null) {
            for (const key of Object.keys(details)) {
              const val = details[key];
              if (val == null) continue;
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              body += this.rtfLabelValue(
                label,
                typeof val === 'object' ? JSON.stringify(val) : String(val)
              );
            }
          }
        }

        // ── Patient Notes ─────────────────────────────────────────────────────
        if (patientNotes.length) {
          body += this.rtfSeparator();
          body += this.rtfHeading(`Patient Notes  (${patientNotes.length} Record${patientNotes.length > 1 ? 's' : ''})`);

          patientNotes.forEach((note: any, idx: number) => {
            const noteDate = note.date
              ? new Date(note.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              : '';
            const noteTime = note.time
              ? `  ${note.time}${note.period ? ' ' + note.period : ''}`
              : '';
            body += this.rtfSubheading(`Note ${idx + 1}  ·  ${noteDate}${noteTime}`);
            if (note.master_notes) {
              body += this.htmlToRtf(note.master_notes);
            }
            if (idx < patientNotes.length - 1) body += this.rtfSeparator();
          });
        }

        const ref = order?.order_number ?? order?.order_id ?? 'report';
        this.saveRtf(body, `medical-necessity-${ref}.rtf`);
      })
      .catch(() => {
        this.spinnerService.hide();
        this.toastr.error('Failed to generate Medical Necessity report');
      });
  }

  downloadActionItemPdf(item: any): void {
    this.spinnerService.show();
    this.medicalNecessityPdfService
      .generate({
        item,
        patientId: this.patientID,
        patientName: this.patientName,
        patientMrn: this.patientMrn,
        providerName: this.providerName,
      })
      .then(() => {
        this.spinnerService.hide();
      })
      .catch(() => {
        this.spinnerService.hide();
        this.toastr.error('Failed to generate PDF report');
      });
  }

  downloadDoctorNotesPdf(): void {
    if (!this.parsedData) return;
    this.clinicalDocumentsPdfService.generateDoctorNotesPdf(this.parsedData, {
      patientName: this.patientName,
      patientMrn: this.patientMrn,
      providerName: this.providerName,
      dateOfService: this.dateOfService,
    });
  }

  downloadDoctorNotesRtf(): void {
    if (!this.parsedData) return;
    const dn = this.parsedData?.doctor_notes ?? {};
    const rec = this.recommendationsData;

    const section = (label: string, val: any) =>
      val ? this.rtfHeading(label) + this.rtfPara(String(val)) : '';

    const listSection = (label: string, items: any[], render: (i: any) => string) =>
      items?.length
        ? this.rtfHeading(label) + items.map(i => this.rtfBullet(render(i))).join('')
        : '';

    const assessPoints = (this.Assessmentpoints ?? []).filter(p => p?.length);

    const body = [
      this.rtfTitle('Doctor Notes'),
      section('History of Present Illness', dn['History Of Present Illness']),
      section('Physical Examination', dn['Physical Examination']),
      section('Diagnostics', dn['Diagnostics']),
      assessPoints.length
        ? this.rtfHeading('Assessment / Plan') + assessPoints.map(p => this.rtfBullet(p)).join('')
        : '',
      this.rtfSeparator(),
      this.rtfHeading('Recommendations'),
      listSection('Tests', rec.tests, i => [i.related_to, i.primary, i.secondary].filter(Boolean).join(' – ')),
      listSection('Medications', rec.medications, i => [i.related_to, i.primary, i.secondary].filter(Boolean).join(' – ')),
      listSection('Treatments', rec.treatments, i => [i.related_to, i.primary, i.secondary].filter(Boolean).join(' – ')),
      listSection('Risk Factors', rec.risk_factors, i => [i.related_to, i.primary, i.secondary].filter(Boolean).join(' – ')),
      rec.considerations ? this.rtfHeading('Considerations') + this.rtfPara(rec.considerations) : '',
      listSection('Action Items', this.parsedData?.action_items ?? [], i => [i.actions, i.description].filter(Boolean).join(' ')),
    ].join('');

    this.saveRtf(body, 'doctor-notes.rtf');
  }

  downloadBillingPdf(): void {
    if (!this.billingData) return;
    this.clinicalDocumentsPdfService.generateBillingSummaryPdf(this.billingData, {
      patientName: this.patientName,
      patientMrn: this.patientMrn,
      providerName: this.providerName,
      dateOfService: this.dateOfService,
    });
  }

  // icd10_codes entries can be either legacy plain code strings or the current AI prompt's
  // { code, reason } object shape (see openAi.js's getBillingAnalysisFromTranscription output
  // contract) — normalize either into just the code string before joining, so older stored
  // records and freshly generated ones both render/export correctly instead of "[object Object]".
  private formatIcdCodes(codes: any[] | null | undefined, separator: string): string {
    return (codes ?? [])
      .map((icd: any) => (icd && typeof icd === 'object' ? icd.code ?? '' : icd ?? ''))
      .join(separator);
  }

  // Sorts cpt_codes/add_on_codes (and each entry's nested icd10_codes) alphabetically by code,
  // in place, so the table display, PDF/RTF/CSV exports all show a consistent, sorted order —
  // they all read from this same billingData object.
  private sortBillingCodes(data: any): void {
    if (!data) return;
    const icdCode = (icd: any) => (icd && typeof icd === 'object' ? icd.code ?? '' : icd ?? '');
    const sortEntries = (entries: any[]) => {
      if (!Array.isArray(entries)) return;
      entries.forEach((c: any) => {
        if (Array.isArray(c?.icd10_codes)) {
          c.icd10_codes.sort((a: any, b: any) => icdCode(a).localeCompare(icdCode(b)));
        }
      });
      entries.sort((a: any, b: any) => (a.cpt_code || '').localeCompare(b.cpt_code || ''));
    };
    sortEntries(data.cpt_codes);
    sortEntries(data.add_on_codes);
  }

  downloadBillingRtf(): void {
    if (!this.billingData) return;

    const codeSection = (label: string, codes: any[]) => {
      if (!codes?.length) return '';
      const items = codes.map(c => {
        const icd = this.formatIcdCodes(c.icd10_codes, ', ');
        return this.rtfBullet(`${c.cpt_code}  |  ${c.description}  |  ICD-10: ${icd}  |  ${c.reason ?? ''}`);
      }).join('');
      return this.rtfHeading(label) + items;
    };

    const body = [
      this.rtfTitle('Billing Analysis'),
      codeSection('CPT Codes', this.billingData.cpt_codes),
      codeSection('Add-On Codes', this.billingData.add_on_codes),
      this.billingData.billing_report
        ? this.rtfSeparator() + this.rtfHeading('Billing Report') + this.rtfPara(this.billingData.billing_report)
        : '',
    ].join('');

    this.saveRtf(body, 'billing-analysis.rtf');
  }

  downloadTranscriptionRtf(): void {
    if (!this.transcriptionData) return;
    const body = this.rtfTitle('Transcription') + this.rtfPara(this.transcriptionData);
    this.saveRtf(body, 'transcription.rtf');
  }

  downloadTranscriptionPdf(): void {
    if (!this.transcriptionData) return;
    this.clinicalDocumentsPdfService.generateTranscriptionPdf(this.transcriptionData, {
      patientName: this.patientName,
      patientMrn: this.patientMrn,
      providerName: this.providerName,
      dateOfService: this.dateOfService,
    });
  }

  downloadBillingCsv(): void {
    if (!this.billingData) return;
    const csv = this.convertToCsv(this.billingData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'billing-analysis.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  private convertToCsv(data: any): string {
    const escape = (val: any): string => {
      const str = val === null || val === undefined ? '' : String(val);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const codesHeaders = ['CPT Code', 'Description', 'ICD-10 Codes', 'Reason'];
    const codeToRow = (entry: any): string =>
      [
        escape(entry?.cpt_code ?? entry?.code ?? ''),
        escape(entry?.description ?? ''),
        escape(
          Array.isArray(entry?.icd10_codes)
            ? this.formatIcdCodes(entry.icd10_codes, '; ')
            : entry?.icd10_codes ?? ''
        ),
        escape(entry?.reason ?? ''),
      ].join(',');

    const sections: string[] = [];

    const cptCodes: any[] = Array.isArray(data?.cpt_codes) ? data.cpt_codes : [];
    if (cptCodes.length > 0) {
      sections.push('CPT Codes');
      sections.push(codesHeaders.join(','));
      cptCodes.forEach((entry) => sections.push(codeToRow(entry)));
    }

    const addOnCodes: any[] = Array.isArray(data?.add_on_codes) ? data.add_on_codes : [];
    if (addOnCodes.length > 0) {
      sections.push('');
      sections.push('Add-On Codes');
      sections.push(codesHeaders.join(','));
      addOnCodes.forEach((entry) => sections.push(codeToRow(entry)));
    }

    if (data?.billing_report) {
      sections.push('');
      sections.push('Billing Report');
      sections.push(escape(data.billing_report));
    }

    return sections.join('\n');
  }

  private copyTextToClipboard(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.toastr.success('Copied to clipboard');
  }
}
